import { Link, Head, router, usePage } from '@inertiajs/react'
import { ArrowRight, Globe, Heart, Mail, Phone } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import EventCard from '@/components/EventCard'
import LatestUpdatesCard from '@/components/organization_profiles/LatestUpdatesCard'
import type { DetailOrganization, EventListItem, SharedProps } from '@/types'

type ProfilePost = {
  id: string
  body: string
  pinned_at: string | null
  edited_at: string | null
  created_at: string
  author: { name: string }
  image_url: string | null
  linked_event: {
    id: string
    slug: string
    title: string
    date: string | null
    page_kind: 'event' | 'fundraiser'
  } | null
  reactions_summary: Record<string, number>
  current_user_reaction: string | null
}

const PLACEHOLDER_COLORS = [
  'bg-primary/10',
  'bg-secondary/20',
  'bg-zinc-100 dark:bg-zinc-800',
  'bg-primary/5',
  'bg-secondary/10',
  'bg-zinc-50 dark:bg-zinc-800/50',
]

function hashIndex(id: string, len: number): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % len
}

function orgInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

type OrganizationProfileProps = {
  organization: DetailOrganization
  events: EventListItem[]
  fundraisers: EventListItem[]
  follower_count: number
  is_following: boolean
  recent_posts: ProfilePost[]
  is_org_member: boolean
}

function FundraiserCard({ fundraiser }: { fundraiser: EventListItem }) {
  const primaryCause = fundraiser.organization?.primary_cause || fundraiser.primary_cause

  return (
    <div className="group h-full overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:ring-primary/20 hover:shadow-lg">
      <div className="relative h-44 overflow-hidden">
        {fundraiser.cover_image_url ? (
          <img src={fundraiser.cover_image_url} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className={`flex h-full w-full items-center justify-center ${PLACEHOLDER_COLORS[hashIndex(fundraiser.id, PLACEHOLDER_COLORS.length)]}`}>
            <Heart className="size-8 text-primary/25" fill="currentColor" />
          </div>
        )}
        {primaryCause && (
          <div className="absolute bottom-3 left-3">
            <Badge className="bg-white/90 text-foreground shadow-sm backdrop-blur-sm hover:bg-white/90">{primaryCause}</Badge>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold leading-snug line-clamp-2 text-balance">{fundraiser.title}</h3>
        {fundraiser.description && (
          <p className="mt-1.5 text-sm leading-snug text-foreground line-clamp-3">{fundraiser.description}</p>
        )}
        <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
          <span className="inline-flex items-center gap-1.5 text-base font-semibold text-primary">
            <Heart className="size-4" fill="currentColor" />
            Donate
          </span>
          <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  )
}

export default function OrganizationProfileShow({
  organization,
  events,
  fundraisers,
  follower_count,
  is_following,
  recent_posts,
  is_org_member,
}: OrganizationProfileProps) {
  const { current_user, app_url } = usePage<SharedProps>().props
  const ogImage = organization.logo_url || `${app_url}/meta.png`
  const ogUrl = `${app_url}/o/${organization.slug}`
  const ogDescription = organization.description ? organization.description.slice(0, 160) : `${organization.name} — organization profile on Gala Season.`

  return (
    <>
      <Head title={organization.name}>
        <meta head-key="description" name="description" content={ogDescription} />
        <meta head-key="og:title" property="og:title" content={`${organization.name} — Gala Season`} />
        <meta head-key="og:description" property="og:description" content={ogDescription} />
        <meta head-key="og:type" property="og:type" content="profile" />
        <meta head-key="og:url" property="og:url" content={ogUrl} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content={`${organization.name} — Gala Season`} />
        <meta head-key="twitter:description" name="twitter:description" content={ogDescription} />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            href="/"
            className="text-base text-muted-foreground hover:text-foreground"
          >
            &larr; Back to events
          </Link>
        </div>

        {/* Org header */}
        <div className="mb-10 flex items-start gap-5">
          {organization.logo_url ? (
            <img
              src={organization.logo_url}
              alt={`${organization.name} logo`}
              className="h-20 w-20 shrink-0 rounded-xl object-cover outline-1 -outline-offset-1 outline-black/5"
            />
          ) : (
            <div className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-xl text-3xl font-semibold text-primary ${PLACEHOLDER_COLORS[hashIndex(organization.id, PLACEHOLDER_COLORS.length)]}`}>
              {orgInitials(organization.name)}
            </div>
          )}

          <div className="min-w-0">
            <h1 className="text-4xl font-semibold tracking-tight text-balance">
              {organization.name}
            </h1>
            {organization.verified && <VerifiedBadge variant="pill" className="mt-1" />}

            {/* Stats strip */}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-base text-muted-foreground">
              <span><span className="font-semibold text-foreground">{follower_count}</span> {follower_count === 1 ? 'follower' : 'followers'}</span>
              <span>·</span>
              <span><span className="font-semibold text-foreground">{events.length}</span> {events.length === 1 ? 'event' : 'events'}</span>
              {fundraisers.length > 0 && (
                <>
                  <span>·</span>
                  <span><span className="font-semibold text-foreground">{fundraisers.length}</span> {fundraisers.length === 1 ? 'fundraiser' : 'fundraisers'}</span>
                </>
              )}
              {organization.primary_cause && (
                <>
                  <span>·</span>
                  <span>{organization.primary_cause}</span>
                </>
              )}
            </div>

            {/* Contact info */}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-base text-muted-foreground">
              {organization.website && (
                <a
                  href={organization.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-foreground"
                >
                  <Globe className="size-4 shrink-0" />
                  {organization.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {organization.email && (
                <a
                  href={`mailto:${organization.email}`}
                  className="flex items-center gap-1.5 hover:text-foreground"
                >
                  <Mail className="size-4 shrink-0" />
                  {organization.email}
                </a>
              )}
              {organization.phone && (
                <a
                  href={`tel:${organization.phone}`}
                  className="flex items-center gap-1.5 hover:text-foreground"
                >
                  <Phone className="size-4 shrink-0" />
                  {organization.phone}
                </a>
              )}
            </div>

            {/* Actions row */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {organization.donation_url && (
                <Button asChild>
                  <a
                    href={organization.donation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Donate
                  </a>
                </Button>
              )}

              {current_user ? (
                <Button
                  variant={is_following ? 'default' : 'outline'}
                  onClick={() => {
                    if (is_following) {
                      router.delete(`/o/${organization.slug}/follow`, { preserveScroll: true })
                    } else {
                      router.post(`/o/${organization.slug}/follow`, {}, { preserveScroll: true })
                    }
                  }}
                >
                  {is_following ? 'Following' : 'Follow'}
                </Button>
              ) : (
                <Button variant="outline" disabled>
                  Follow
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="mb-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-10 lg:col-span-2">
            {/* Description */}
            {organization.description && (
              <div>
                <p className="text-muted-foreground text-pretty">
                  {organization.description}
                </p>
              </div>
            )}

            {/* Causes & Professional Networks */}
            {(organization.causes.length > 0 || organization.industries.length > 0) && (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {organization.causes.length > 0 && (
                  <div>
                    <h2 className="mb-3 text-xl font-semibold">Causes We Support</h2>
                    <div className="flex flex-wrap gap-2">
                      {organization.causes.map((cause) => (
                        <Badge
                          key={cause}
                          variant={
                            cause === organization.primary_cause
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {cause}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {organization.industries.length > 0 && (
                  <div>
                    <h2 className="mb-3 text-xl font-semibold">
                      Professional Networks
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {organization.industries.map((industry) => (
                        <Badge key={industry} variant="secondary">
                          {industry}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <LatestUpdatesCard posts={recent_posts} isOrgMember={is_org_member} canReact={!!current_user} />
            </div>
          </aside>
        </div>

        {fundraisers.length > 0 && (
          <div className="mb-12">
            <h2 className="mb-6 text-3xl font-semibold">
              Fundraisers by {organization.name}
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-grid">
              {fundraisers.map((fundraiser) => (
                <Link key={fundraiser.id} href={`/events/${fundraiser.slug}`} className="animate-fade-up">
                  <FundraiserCard fundraiser={fundraiser} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Events section */}
        <div>
          <h2 className="mb-6 text-3xl font-semibold">
            Events by {organization.name}
          </h2>

          {events.length === 0 ? (
            <p className="text-muted-foreground">No upcoming events.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-grid">
              {events.map((event) => (
                <Link key={event.id} href={`/events/${event.slug}`} className="animate-fade-up">
                  <EventCard event={event} showOrganization={false} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
